/**
 * DEFUSE S15 — the ONE typed property-edit WRITER (edit-write collapse) — CORE.
 *
 * Today a single `properties/apply` fans out to TWO writers fed the SAME
 * `Record<string,string>` payload: the string-bag mutation (propertyReducer) AND
 * a re-translation of that rounded string onto the typed document (geometryReducer
 * → `applySimpleGeometryPropertyChanges` reads `readLineState`/`readCircleState`/
 * `readArcState` off the 3-decimal preview bag, then the geometryReducer patches
 * the loss back up with `reconcileLinePatchWithDocument` /
 * `reconcileCircleArcPatchWithDocument`). That round-trip-through-strings is the
 * bug factory behind the #416/#417/#418 raw-precision / Z-drop churn.
 *
 * This writer edits the TYPED `DrawingEntity` field IN PLACE via the S15 typed
 * property descriptor (`getTypedPropertyDescriptor`) — it reads the full-precision
 * typed value, runs the descriptor's `toRaw` inverse, validates numeric
 * constraints, and writes the raw value back at `typedFieldRef.path` while
 * preserving every OTHER field + per-point metadata (M7). Because the untouched
 * axes are never round-tripped through the rounded preview, the raw-precision /
 * Z-preserve invariant holds BY CONSTRUCTION — the reconcile scaffolds become
 * dead. Side-effect cells (HATCH Pattern → solidFill reset, Viewport view fields)
 * are NOT plain field sets, so the writer RETURNS a directive for the caller to
 * route through the real mutator instead of patching the path directly.
 *
 * ПРИОРИТЕТ #1: this is the package-level CAD-core writer; apps/web projects onto
 * it (the geometryReducer simple-geometry cutover), it does not re-implement the
 * write semantics. `applyTypedPropertyEditWithDescriptor` is the pure core; the
 * `applyTypedPropertyEdit(label|descriptor)` overload resolves the descriptor.
 *
 * PLACEMENT (ПРИОРИТЕТ #2, S15 Slice A file split): this CORE module is the LEAF
 * — the S15 cutover BUILDERS (`buildTypedSimpleGeometryEdit` /
 * `buildTypedCommonEdit` / `buildTypedColorEdit` / `buildTypedExtendedGeometryEdit`)
 * live in `typedPropertyWriter.ts`, which imports this core and RE-EXPORTS it, so
 * every existing import path (`./typedPropertyWriter`, the package barrel) keeps
 * resolving unchanged — the exact split pattern `geometryEntityTypes.ts` /
 * `descriptors/types.ts` use to stay under the file-size cap with zero dep cycles.
 */

import type { EntityType } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import type {
  EditorKind,
  NumericConstraint,
  TypedPropertyDescriptor,
} from "./descriptors/types";
import { resolveReadOnly } from "./descriptors/types";
import {
  getTypedPropertyDescriptor,
  resolveDisplay,
} from "./typedPropertyDescriptors";

/** Outcome of a single typed property edit. */
export type TypedPropertyEditStatus =
  | "applied" // the typed field changed
  | "noop" // the submitted display equals the current value (preserve raw precision)
  | "rejected" // unknown/read-only descriptor, malformed input, or constraint violation
  | "side-effect" // a mutator-routed cell — caller applies via the named mutator
  | "deferred"; // editable in AutoCAD but no PLAIN typed home (empty path) — the
// write-back is a graph-backed reconciler that is a documented S15 follow-up
// (Multileader Arrow/Landing → leaderRoots vertices, Revision Cloud corners → the
// vertex AABB, Dimension line → defPoint-vs-textMidpoint). Today these are NOT
// persisted to the document either (unmapped in `labelToFieldByType`), so the
// writer no-ops the DOCUMENT rather than corrupting the entity with a bogus set.

/**
 * A mutator-routed edit the writer will NOT apply as a plain field set. The
 * caller routes it through the real mutator identified by `mutator` (the
 * `constraints.sideEffects` value — e.g. HATCH Pattern → solidFill / gradient /
 * customPatternLines reset). `raw` is the already-validated typed value.
 */
export type TypedPropertySideEffect = {
  mutator: string;
  label: string;
  raw: unknown;
  display: string;
};

export type TypedPropertyEditResult = {
  /** the edited entity (unchanged for every non-`applied` status). */
  entity: DrawingEntity;
  status: TypedPropertyEditStatus;
  /** present only for `status === "side-effect"`. */
  sideEffect?: TypedPropertySideEffect;
};

/**
 * Default display→raw parse for a descriptor WITHOUT an explicit `toRaw` — the
 * exact inverse the census `defaultParse` documents: `Number` for numeric editors
 * (a non-finite result is a reject sentinel handled by the caller), a truthy-token
 * set for booleans, verbatim string otherwise. Kept in lock-step with the census
 * `M2`/`M4` `defaultParse` so the writer's un-`toRaw`'d fields round-trip identically.
 */
function defaultParse(editorKind: EditorKind, display: string): unknown {
  switch (editorKind) {
    case "number":
    case "point-coord":
    case "enum":
      return Number(display);
    case "boolean":
      return ["yes", "true", "on", "1"].includes(display.trim().toLowerCase());
    default:
      return display;
  }
}

/** A `toRaw` reject sentinel — `null` / `undefined` / a non-finite number (never a
 *  corrupt raw value). Mirrors the census `M4` reject contract. */
function isRejectSentinel(raw: unknown): boolean {
  return (
    raw === null ||
    raw === undefined ||
    (typeof raw === "number" && !Number.isFinite(raw))
  );
}

/**
 * Coerce + validate a raw numeric against the descriptor's `NumericConstraint`.
 * `int` fields are rounded (mirrors the extended-array `Math.round` legacy); a
 * `min` / `max` / `exclusiveMin` violation REJECTS (mirrors the simple-geometry
 * `parsePositiveNumber` → Radius must be `> 0`). Non-numeric raws pass through.
 */
function coerceNumeric(
  raw: unknown,
  numeric: NumericConstraint | undefined,
): { value: unknown; rejected: boolean } {
  if (!numeric || typeof raw !== "number") return { value: raw, rejected: false };
  let v = raw;
  if (numeric.int) v = Math.round(v);
  if (numeric.exclusiveMin !== undefined && !(v > numeric.exclusiveMin)) {
    return { value: v, rejected: true };
  }
  if (numeric.min !== undefined && v < numeric.min) return { value: v, rejected: true };
  if (numeric.max !== undefined && v > numeric.max) return { value: v, rejected: true };
  return { value: v, rejected: false };
}

/** Walk a dotted path on a value (no array-map support — that is `writeTypedField`). */
function readPath(obj: unknown, path: string): unknown {
  let cursor: unknown = obj;
  for (const segment of path.split(".")) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/**
 * A point base is COMPLETE iff it already carries a finite `x` AND `y` (z stays
 * optional per `drawingPointOptionalZSchema`). A single-axis point-coord edit can
 * only preserve the sibling axis when the base is a complete point; an ABSENT /
 * partial base would `setPath` a schema-invalid partial (`drawingPointSchema`
 * requires both `x` and `y`). The P1 complete-point guard rejects the write in
 * that case rather than fabricate a corrupt point.
 */
function isCompletePointBase(base: unknown): boolean {
  if (base === null || typeof base !== "object") return false;
  const p = base as Record<string, unknown>;
  return (
    typeof p.x === "number" &&
    Number.isFinite(p.x) &&
    typeof p.y === "number" &&
    Number.isFinite(p.y)
  );
}

/**
 * Immutable deep-set of `value` at a dotted `path`, cloning only the spine so the
 * sibling fields (a point's untouched Y/Z, per-point metadata) are preserved by
 * reference. Returns a NEW entity — never mutates the input.
 */
function setPath<T>(obj: T, path: string, value: unknown): T {
  const segments = path.split(".");
  const clone = { ...(obj as Record<string, unknown>) };
  let cursor = clone;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const seg = segments[i]!;
    const child = cursor[seg];
    cursor[seg] =
      child != null && typeof child === "object"
        ? { ...(child as Record<string, unknown>) }
        : {};
    cursor = cursor[seg] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]!] = value;
  return clone as T;
}

/**
 * Write the raw value at `typedFieldRef.path`.
 *  - plain dotted path (`start.x`, `radius`, `center.z`, `color`) → immutable
 *    deep-set preserving siblings.
 *  - array-map path (`vertices[].bulge`) → distribute the raw `value[]` onto each
 *    `arr[i].field`, preserving every other per-vertex field (M7). `toRaw`
 *    count-locks the list, so `value.length === arr.length` on a non-reject raw.
 *
 * Exported for the S15 cutover builders (`typedPropertyWriter.ts` —
 * `decideColorEdit` writes the decided union through the same one-truth setter).
 */
export function writeTypedField(
  entity: DrawingEntity,
  path: string,
  raw: unknown,
): DrawingEntity {
  const mapAt = path.indexOf("[].");
  if (mapAt >= 0) {
    const arrPath = path.slice(0, mapAt);
    const field = path.slice(mapAt + "[].".length);
    const arr = readPath(entity, arrPath);
    if (!Array.isArray(arr) || !Array.isArray(raw)) return entity;
    const nextArr = arr.map((el, i) => ({
      ...(el as Record<string, unknown>),
      [field]: (raw as unknown[])[i],
    }));
    return setPath(entity, arrPath, nextArr);
  }
  return setPath(entity, path, raw);
}

/**
 * The BASE ARRAY field a `typedFieldRef.path` ultimately writes for the purpose
 * of a `patchEntityById` shallow-merge patch — the path's first segment with any
 * array-map `[]` suffix STRIPPED.
 *
 * S15 Slice A BLOCKER-A: the builders used a bare `path.split(".")[0]`, which for
 * the array-map path `vertices[].bulge` (LWPOLYLINE Bulges) yielded the BOGUS key
 * `"vertices[]"` — `entity["vertices[]"]` is `undefined`, so the collected patch
 * carried `{ "vertices[]": undefined }` and the applied bulge edit was DROPPED
 * SILENTLY (TS/build-blind: the record index is stringly). Stripping to the real
 * base array name (`vertices`) makes the patch carry the rebuilt vertex array.
 * Applies to EVERY array-map descriptor (the registry sweep in
 * `typedPropertyWriter.extendedGeometry.test.ts` proves `vertices[].bulge` is the
 * only one today and pins the invariant for future ones).
 */
export function typedFieldPatchTopField(path: string): string {
  const top = path.split(".")[0]!;
  return top.endsWith("[]") ? top.slice(0, -2) : top;
}

/** Deep value-equality for the no-op backstop (numbers strict, arrays/objects
 *  structural). Cheap: only reached after the display-equality short-circuit.
 *  Exported for the S15 cutover builders (`decideColorEdit`). */
export function rawEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * The writer's PARSE + CONSTRAINT acceptance gate as a standalone step —
 * display string → raw typed value via the descriptor inverse (`toRaw`, or the
 * editorKind `defaultParse`), reject-sentinel check, then numeric-constraint
 * coercion/rejection (min / max / exclusiveMin / int round). These ARE the
 * writer's own steps 3–4 (`applyTypedPropertyEditWithDescriptor` calls this),
 * exported so the SHELL preview validator (`extendedPropertyMutations` via
 * `typedExtendedGeometryShellRejects`) mirrors the DOCUMENT's accept/reject
 * verdict through the SAME code path — never a hand-copied min/int rule
 * (@codex P2 ×2 on c8394b70: ArrayRect Rows/Columns `min:1+int` and LWPOLYLINE
 * `Constant width min:0` were writer-rejected but shell-accepted, so the
 * Properties grid/audit showed a value the typed document refused).
 *
 * `entity` is optional exactly like `toRaw`'s own parameter: WITH the live
 * document entity the cross-field validations apply (Bulges/Vertices count
 * locks); WITHOUT it (the shell holds only the string bag) those locks are
 * skipped HERE — parsing can only get MORE permissive, never stricter, so the
 * shell mirror can never over-reject — and the shell's own preview-derived
 * count locks (`isWellFormedCoordinateProperty`) keep owning them.
 */
export function parseTypedPropertyInput(
  descriptor: TypedPropertyDescriptor,
  displayValue: string,
  entity?: DrawingEntity,
): { raw: unknown; rejected: boolean } {
  // S15 Slice C review BLOCK (P2, parity break) — a CLEARED numeric / point-coord
  // cell REJECTS (→ the no-op-preserve path a rejected value takes), mirroring the
  // legacy `parseFiniteNumeric("")→null→omit` the owned-label skip-guard removed
  // AND AutoCAD's revert-on-empty. Both `defaultParse` (`Number("")`) and a numeric
  // `toRaw` would otherwise coerce an empty/whitespace display to a finite 0 and
  // WRITE it (Text Height→0 = the text vanishes; Dim Text height→0; Rotation→0).
  // Scoped to `number` + `point-coord` ONLY — an empty `text` Contents is a
  // legitimate clear, `enum` is deferred, `boolean` parses truthy-tokens. Runs on
  // the SHARED gate so the shell mirror (`typedExtendedGeometryShellRejects`) and
  // the document writer reject the empty cell in lockstep, exactly like
  // `parseFiniteNumeric`'s load-bearing empty-after-trim guard.
  if (
    (descriptor.editorKind === "number" ||
      descriptor.editorKind === "point-coord") &&
    displayValue.trim() === ""
  ) {
    return { raw: null, rejected: true };
  }
  const parsed = descriptor.toRaw
    ? descriptor.toRaw(displayValue, entity)
    : defaultParse(descriptor.editorKind, displayValue);
  if (isRejectSentinel(parsed)) return { raw: parsed, rejected: true };
  const { value, rejected } = coerceNumeric(parsed, descriptor.constraints?.numeric);
  return { raw: value, rejected };
}

/**
 * The writer's PRESERVE-RAW no-op verdict (steps 5+6 of
 * {@link applyTypedPropertyEditWithDescriptor}) as a standalone predicate: does
 * writing `displayValue` (parsed to `raw`) leave `descriptor`'s field on `entity`
 * UNCHANGED? True when
 *   (5) the submitted display is display-value-EQUIVALENT to the current display
 *       — numeric: `Number`-equal (so a formatNumber-rounded re-submit of a
 *       full-precision value PRESERVES the raw, #317/#418), else byte-equal; OR
 *   (6) the parsed `raw` deep-equals the current typed value (a display-TOKEN
 *       variant — "yes" for a stored `true`, or "1.2349" == a stored full-
 *       precision raw whose rounded display differs).
 *
 * Extracted so the SIDE-EFFECT builder can gate a mutator-routed directive with
 * the EXACT SAME guard the plain path uses. For side-effects the `entity` passed
 * here is the PRE-MUTATION document (so a stale unchanged COUPLED row — an
 * imported hatch's `Solid="No"` dragged in the full-bag single-select apply,
 * ordered after `Pattern` — is compared against the ORIGINAL `solidFill` and
 * DROPPED, never overriding a sibling directive's derived `solidFill=true`).
 * `false` for a descriptor with no `typedFieldRef` (nothing to compare).
 */
export function isTypedFieldEditNoop(
  descriptor: TypedPropertyDescriptor,
  entity: DrawingEntity,
  displayValue: string,
  raw: unknown,
): boolean {
  if (!descriptor.typedFieldRef) return false;
  const currentDisplay = resolveDisplay(descriptor, entity);
  const current = descriptor.typedFieldRef.read(entity);
  if (descriptor.editorKind === "number" || descriptor.editorKind === "point-coord") {
    const inNum = Number(displayValue);
    const curNum = Number(currentDisplay);
    if (Number.isFinite(inNum) && Number.isFinite(curNum) && inNum === curNum) {
      return true;
    }
  } else if (currentDisplay === displayValue) {
    return true;
  }
  return rawEqual(current, raw);
}

/**
 * The pure typed-write core. Resolves the display value against a descriptor,
 * validates it, and either applies the field, no-ops, rejects, or hands back a
 * side-effect directive. Never mutates `entity`.
 */
export function applyTypedPropertyEditWithDescriptor(
  entity: DrawingEntity,
  descriptor: TypedPropertyDescriptor,
  displayValue: string,
): TypedPropertyEditResult {
  // 1. writability — read-only-calc / ui-projection-deferred / explicitly
  //    read-only common props (per-kind CHPROP gate) never accept a write.
  //    `resolveReadOnly` (not a bare truthy read of `constraints.readOnly`)
  //    so an entity-STATE-dependent predicate is evaluated against THIS
  //    entity, not treated as an always-truthy function reference (currently
  //    only a STATIC `constraints.readOnly: true` exists in the registry —
  //    ArrayRect `Associative`, S16-ARRAYRECT-READONLY-ALIGN — but the
  //    resolver supports a future entity-state predicate too).
  if (
    descriptor.channel !== "editable-typed-field" ||
    resolveReadOnly(descriptor, entity)
  ) {
    return { entity, status: "rejected" };
  }

  // 2. side-effect cells route through the caller's real mutator FIRST — the
  //    write is NOT a plain field set (dependent fields must be rebuilt), so the
  //    named mutator (hatchPatternMutator / viewportScaleMutator / …) owns the
  //    value parsing + validation. Routing is unconditional (never pre-reject a
  //    format the plain parser can't handle, e.g. a "1:100" viewport Scale); the
  //    directive carries a best-effort `raw` plus the raw `display`.
  if (descriptor.constraints?.sideEffects) {
    const raw = descriptor.toRaw
      ? descriptor.toRaw(displayValue, entity)
      : defaultParse(descriptor.editorKind, displayValue);
    return {
      entity,
      status: "side-effect",
      sideEffect: {
        mutator: descriptor.constraints.sideEffects,
        label: descriptor.label,
        raw,
        display: displayValue,
      },
    };
  }

  // 3+4. display → raw via the descriptor inverse (or the editorKind default) +
  //      numeric constraint coercion / rejection (Radius > 0, Ratio ∈ (0,1],
  //      int …) — the SHARED `parseTypedPropertyInput` gate; the SHELL mirror
  //      (`typedExtendedGeometryShellRejects`) runs the same function, so the
  //      two sides accept/reject identically by construction.
  const { raw, rejected } = parseTypedPropertyInput(descriptor, displayValue, entity);
  if (rejected) return { entity, status: "rejected" };

  if (!descriptor.typedFieldRef) return { entity, status: "rejected" };

  // 4b. an EMPTY path is a DERIVED (graph-backed) coordinate with no plain typed
  //     home — the write-back reconciler is a documented S15 follow-up. Do NOT
  //     `setPath("")` (that fabricates a bogus `""` key); leave the document
  //     unchanged, exactly as today (these fields are unmapped in the live
  //     `labelToFieldByType`, so they never reached the document either).
  if (descriptor.typedFieldRef.path === "") return { entity, status: "deferred" };

  // 5+6. PRESERVE-RAW no-op (display-value equivalence + structural backstop) —
  //      the shared `isTypedFieldEditNoop` predicate (extracted so the SIDE-EFFECT
  //      builder gates a mutator-routed directive with the SAME guard). See its
  //      doc for the #317/#418 precision-drop semantics.
  if (isTypedFieldEditNoop(descriptor, entity, displayValue, raw)) {
    return { entity, status: "noop" };
  }

  // 6b. COMPLETE-POINT guard (S15 Slice C review BLOCK, P1 — prod schema-
  //     corruption). A point-coord edit targets ONE axis of a nested point
  //     (`base.axis`). If the OPTIONAL base is currently ABSENT (a radial/diameter
  //     dim with no `extLine1Point`; a default-justified TEXT with no
  //     `alignmentPoint`) — or malformed — a plain `setPath` fabricates a PARTIAL
  //     point `{ x: 5 }` with no sibling `y`. `drawingPointSchema` requires BOTH
  //     x and y, so the entity turns schema-invalid: dev/test
  //     `assembleDrawingDocumentInPlace` THROWS, PROD silently persists the
  //     corrupt point → a broken DXF export. Absent-base point CREATION (seeding
  //     the whole point) is a dedicated follow-up slice; until then REJECT
  //     (preserve) rather than corrupt. The ALWAYS-present bases (Text/MText
  //     `insertionPoint`, Array `basePoint`, Point `location`, Ellipse `center` /
  //     `majorAxisEndpoint`) never trip this — their base is a complete point by
  //     schema, so the clone-and-set writes a complete point. Belt-and-suspenders
  //     BEHIND the owned-set exclusion so no FUTURE optional-base coord left owned
  //     by mistake can fabricate a partial point.
  if (descriptor.editorKind === "point-coord") {
    const dotAt = descriptor.typedFieldRef.path.lastIndexOf(".");
    if (dotAt > 0) {
      const base = readPath(entity, descriptor.typedFieldRef.path.slice(0, dotAt));
      if (!isCompletePointBase(base)) {
        return { entity, status: "rejected" };
      }
    }
  }

  // 7. write the typed field, preserving siblings + per-point metadata (M7).
  return {
    entity: writeTypedField(entity, descriptor.typedFieldRef.path, raw),
    status: "applied",
  };
}

/**
 * Resolve `labelOrDescriptor` to the kind's typed descriptor and apply the edit.
 * An unknown label rejects. Accepts a resolved descriptor directly so a caller
 * iterating a kind's descriptor set skips the per-label lookup.
 */
export function applyTypedPropertyEdit(
  entity: DrawingEntity,
  labelOrDescriptor: string | TypedPropertyDescriptor,
  displayValue: string,
): TypedPropertyEditResult {
  const descriptor =
    typeof labelOrDescriptor === "string"
      ? getTypedPropertyDescriptor(entity.type as EntityType, labelOrDescriptor)
      : labelOrDescriptor;
  if (!descriptor) return { entity, status: "rejected" };
  return applyTypedPropertyEditWithDescriptor(entity, descriptor, displayValue);
}
